import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@repo/ui";
import { configurationApi } from "~/api";
import { getErrorMessage } from "~/hooks/helpers";
import type { Status } from "~/types/api";

type Props = {
  statuses: Status[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  createStatus: (params: {
    workflowId: number;
    status: { code: number; name: string };
    showToastMessage?: boolean;
  }) => Promise<Status | undefined>;
  isCreating: boolean;
  updateStatus: (params: {
    workflowId: number;
    code: number;
    name: string;
    showToastMessage?: boolean;
  }) => Promise<Status | undefined>;
  isUpdating: boolean;
  deleteStatus: (params: {
    workflowId: number;
    code: number;
    showToastMessage?: boolean;
  }) => Promise<void>;
  isDeleting: boolean;
};

export const useWorkflowStatusMutations = (
  workflowId: number | undefined,
  enabled = true
): Props => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = configurationApi.useQuery(
    "get",
    "/api/workflow/{workflowId}/Status",
    {
      params: { path: { workflowId: workflowId! } },
    },
    {
      enabled: enabled && !!workflowId,
    }
  );

  const createStatusMutation = useMutation({
    mutationFn: async ({
      workflowId,
      status,
    }: {
      workflowId: number;
      status: { code: number; name: string };
      showToastMessage?: boolean;
    }) => {
      const response = await configurationApi.client.POST("/api/workflow/{workflowId}/Status", {
        params: { path: { workflowId } },
        body: status,
      });

      if (response?.error) {
        const error = getErrorMessage(response.error, "Failed to create status");
        throw new Error(error);
      }
      if (response?.response?.status < 200 || response?.response?.status > 299) {
        throw new Error("Unexpected response status: " + response.response.status);
      }

      return response.data;
    },
    onSuccess: (_data, variables) => {
      if (variables.showToastMessage !== false) {
        showToast(
          "success",
          `Status ${variables.status.name} created successfully`,
          `Status ${variables.status.name}`
        );
      }
      queryClient.invalidateQueries({
        queryKey: configurationApi.queryOptions("get", "/api/workflow/{workflowId}/Status", {
          params: { path: { workflowId: workflowId! } },
        }).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: configurationApi.queryOptions("get", "/api/Workflow").queryKey,
      });
    },
    onError: (error: Error, variables) => {
      showToast("error", error.message, `Failed to create status ${variables.status.name}`);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({
      workflowId,
      code,
      name,
    }: {
      workflowId: number;
      code: number;
      name: string;
      showToastMessage?: boolean;
    }) => {
      const response = await configurationApi.client.PATCH(
        "/api/workflow/{workflowId}/Status/{code}/name",
        {
          params: { path: { workflowId, code } },
          body: { name },
        }
      );

      if (response?.error) {
        const error = getErrorMessage(response.error, "Failed to update status");
        throw new Error(error);
      }
      if (response?.response?.status < 200 || response?.response?.status > 299) {
        throw new Error("Unexpected response status: " + response.response.status);
      }

      return response.data;
    },
    onSuccess: (_data, variables) => {
      if (variables.showToastMessage !== false) {
        showToast(
          "success",
          `Status ${variables.name} updated successfully`,
          `Status ${variables.name}`
        );
      }
      queryClient.invalidateQueries({
        queryKey: configurationApi.queryOptions("get", "/api/workflow/{workflowId}/Status", {
          params: { path: { workflowId: workflowId! } },
        }).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: configurationApi.queryOptions("get", "/api/Workflow").queryKey,
      });
    },
    onError: (error: Error, variables) => {
      showToast("error", error.message, `Failed to update status ${variables.name}`);
    },
  });

  const deleteStatusMutation = useMutation({
    mutationFn: async ({
      workflowId,
      code,
    }: {
      workflowId: number;
      code: number;
      showToastMessage?: boolean;
    }) => {
      const response = await configurationApi.client.DELETE(
        "/api/workflow/{workflowId}/Status/{code}",
        {
          params: { path: { workflowId, code } },
        }
      );

      if (response?.error) {
        const error = getErrorMessage(response.error, "Failed to delete status");
        throw new Error(error);
      }
      if (response?.response?.status < 200 || response?.response?.status > 299) {
        throw new Error("Unexpected response status: " + response.response.status);
      }

      return response.data;
    },
    onSuccess: (_data, variables) => {
      if (variables.showToastMessage !== false) {
        showToast(
          "success",
          `Status with code ${variables.code} deleted successfully`,
          `Status Code ${variables.code}`
        );
      }
      queryClient.invalidateQueries({
        queryKey: configurationApi.queryOptions("get", "/api/workflow/{workflowId}/Status", {
          params: { path: { workflowId: workflowId! } },
        }).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: configurationApi.queryOptions("get", "/api/Workflow").queryKey,
      });
    },
    onError: (error: Error, variables) => {
      showToast("error", error.message, `Failed to delete status with code ${variables.code}`);
    },
  });

  return {
    statuses: data || [],
    isLoading,
    error,
    refetch,
    createStatus: createStatusMutation.mutateAsync,
    isCreating: createStatusMutation.isPending,
    updateStatus: updateStatusMutation.mutateAsync,
    isUpdating: updateStatusMutation.isPending,
    deleteStatus: deleteStatusMutation.mutateAsync,
    isDeleting: deleteStatusMutation.isPending,
  };
};
